import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { Pose, POSE_CONNECTIONS } from '@mediapipe/pose';
import {version as poseVersion} from '@mediapipe/pose/package.json'
import classNames from 'classnames';
import React from 'react';
import './App.css';
import { WebPubSubServiceClient } from '@azure/web-pubsub';
import axios from 'axios';
import {v4 as uuidv4} from 'uuid';
import logo from './FittyAI_logo_text_right_2_white.svg'
import {XYPlot, LineSeries, XAxis, YAxis, ChartLabel} from 'react-vis';
import '../node_modules/react-vis/dist/style.css';

const connectionString = 'Endpoint=https://fiaas-pubsub-channel.webpubsub.azure.com;AccessKey=Bh3vZI3JJ6QNONv7PwtOLGHRj0eGVu2AJohC9Yh7qmw=;Version=1.0;'
const identifier = `HUB_NAME_${uuidv4().replaceAll('-', '_')}` //'FittyAI_DEMO';

const WIDTH = 1280 * 7 / 10
const HEIGHT = 720 * 7 / 10

class App extends React.Component {
  state = {
    loaded: false,
    engagementScores: [],
    repCount: null,
    poseLandmarks: null,
    frameIndex: 0,
    squatHeelsRaised: false
  }

  constructor() {
    super()
    this.refVideo = React.createRef();
    this.refCanvas = React.createRef();
  }

  draw(results) {
    const poseLandmarks = results.poseLandmarks.map((landmark, index) => index > 10 ? landmark : { ...landmark, visibility: 0 })
    const poseConnections = POSE_CONNECTIONS.filter(pair => pair.every(landmark => landmark > 10))
    const canvasCtx = this.refCanvas.current.getContext('2d');
    canvasCtx.save();
    canvasCtx.clearRect(0, 0, this.refCanvas.current.width, this.refCanvas.current.height);
    canvasCtx.drawImage(
        results.image, 0, 0, this.refCanvas.current.width, this.refCanvas.current.height);
    drawConnectors(canvasCtx, poseLandmarks, poseConnections,
                   {color: '#F1F1F1', lineWidth: 4});
    drawLandmarks(canvasCtx, poseLandmarks,
                  {color: '#131D47', lineWidth: 2});
    canvasCtx.restore();
  }

  publish(results) {
    const payload = {
      timestamp: new Date(),
      landmarks: results.poseLandmarks,
    }
    try {
      this.state.publisher(payload)
    } catch (error) {
      console.log(error)
    }
  }

  onResults = (results) => {
    this.setState(({loaded, frameIndex}) => {
      const stateUpdate = {
        poseLandmarks: results.poseLandmarks ?? [],
        frameIndex: (frameIndex + 1) % 4
      }
      if (!loaded){
        stateUpdate.loaded = true;
      }

      return stateUpdate
    }, () => {
      if (!results.poseLandmarks) {
        return
      }
      if (this.state.frameIndex === 1) {
        this.publish(results)
      }
      this.draw(results)  
    })
  }

  async setupSubscriber() {
    const serviceClient = new WebPubSubServiceClient(connectionString, `${identifier}_in_out`);
    const token = await serviceClient.getAuthenticationToken();
    const ws = new WebSocket(token.url);
    ws.addEventListener('open', () => console.log('socket connected'));
    ws.addEventListener('error', error => console.error('socket error:', error));
    ws.addEventListener('close', () => console.log('socket closed'));
    return callback => ws.addEventListener('message', callback);
  }

  setupPublisher() {
    const serviceClient = new WebPubSubServiceClient(connectionString, `${identifier}_in`);
    return message => serviceClient.sendToAll(message, { contentType: "application/json" });
  }

  async setupConnection() {
    await axios.get(`https://fitnessintelligence.azurewebsites.net/session/${identifier}/start`)
    const subscriber = await this.setupSubscriber()
    const publisher = this.setupPublisher()
    await new Promise(resolve => this.setState({subscriber, publisher}, resolve))
  }

  onMessage = ({data}) => {
    console.log(data)
    const {engagement_score, rep_count, squat_heels_raised} = JSON.parse(data)

    this.setState(({engagementScores}) => {
      const repCount = Number.parseInt(rep_count)
      const stateUpdate = {
        squatHeelsRaised: squat_heels_raised
      }
      if (!Number.isNaN(repCount)) {
        stateUpdate.repCount = repCount
      }
      if (engagementScores.length > 0 || engagement_score > 0) {
        stateUpdate.engagementScores = [...engagementScores, engagement_score]
      }
      return stateUpdate
    });
  }

  async componentDidMount() {
    await this.setupConnection()

    this.state.subscriber(this.onMessage)

    const pose = new Pose({locateFile: (file) => {
      return `https://cdn.jsdelivr.net/npm/@mediapipe/pose@${poseVersion}/${file}`;
    }})

    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      selfieMode: true
    });
    pose.onResults(this.onResults);
    
    const camera = new Camera(this.refVideo.current, {
      onFrame: async () => {
        await pose.send({image: this.refVideo.current});
      },
      width: WIDTH,
      height: HEIGHT
    });
    camera.start()
  }

  getStatusText() {
    const {engagementScores, poseLandmarks, squatHeelsRaised} = this.state;
    if (poseLandmarks?.slice(11).filter(({visibility}) => visibility > 0.1).length < 14) {
      return 'Move farther away from the camera!'
    }
    if (squatHeelsRaised) {
      return 'Keep your heels on the ground!'
    }
    if (engagementScores.length === 0) {
      return 'No Data!'
    }
    return 'Good job! Keep it up!'
  }

  render() {
    const {loaded, engagementScores, repCount} = this.state

    const data = engagementScores.map((score, index) => ({x: index, y: score}))

    return (
      <div className="App">
        <header className="App-header flex">
          <div className="flex justify-content">
            <img src={logo} alt="Logo" width="400px" />
          </div>
          <div className="flex justify-content">
            <div className="flex justify-content flex-1">
              <video ref={this.refVideo} className={classNames('selfie', loaded ? 'video-hidden' : 'blur')}/>
              <canvas ref={this.refCanvas} width={WIDTH + 'px'} height={HEIGHT + 'px'} className={classNames({'video-hidden': !loaded})}></canvas>
            </div>
            <div className="flex justify-content flex-1" style={{position: 'relative'}}>
              { data.length ? <div style={{position: 'absolute', top: -30, fontSize: 48 }}>Engagement Level</div> : null}
              {
                data.length
                ? <XYPlot height={HEIGHT} width={WIDTH} style={{paddingTop: 40}}>
                    <XAxis color="#f1f1f1" tickValues={[]}/>
                    <YAxis color="#f1f1f1" fill="f1f1f1" tickValues={[]} style={{
                      text: {fill: '#f1f1f1'}
                    }} />
                    <LineSeries data={data} color="#f1f1f1" />
                  </XYPlot>
                : <div className="blur" style={{height: HEIGHT, width: WIDTH, backgroundColor: "#202c6b"}}></div>
              }
            </div>
          </div>
          <div className="flex justify-content">
            <div className="flex-1">
              <span className="status-text">{this.getStatusText()}</span>
            </div>
            <div className="flex-1">
              <span className="status-text">{`${repCount ?? 0} rep(s)`}</span>
            </div>
          </div>
        </header>
      </div>
    );
  }
}

export default App;
